import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { EntityPickerComponent, PickerOption, PickerSearchPage } from '../../../shared/components/form/entity-picker/entity-picker.component';
import { QuoteContentTemplateApiService } from '../services/quote-content-template-api.service';
import { QuoteTemplateApiService } from '../services/quote-template-api.service';
import { QuoteContentTemplateResponse, GenerateQuoteFromTemplateRequest } from '../models/quote-content-template.model';
import { QuoteTemplateResponse } from '../models/quote-template.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { DealApiService } from '../../deals/services/deal-api.service';
import { DealContactApiService } from '../../deals/services/deal-contact-api.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { ProductoApiService } from '../../productos/services/producto-api.service';
import { CurrencyResponse } from '../../productos/models/producto.model';

@Component({
  selector: 'app-quote-content-template-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    EntityPickerComponent
  ],
  templateUrl: './quote-content-template-list.component.html'
})
export class QuoteContentTemplateListComponent implements OnInit {
  templates: QuoteContentTemplateResponse[] = [];
  loading = false;
  error = '';

  // Generate-quote modal state
  showGenerateModal = false;
  generateTemplate: QuoteContentTemplateResponse | null = null;
  selectedDeal: PickerOption | null = null;
  generating = false;

  // Campos opcionales del GenerateQuoteFromTemplateRequest
  selectedContact: PickerOption | null = null;          // idContactPrimary (opcional)
  selectedCurrencyId: number | null = null;             // currencyId (opcional; si se omite hereda la del deal)
  selectedBrandingTemplateId: number | null = null;     // idTemplate (opcional; si se omite usa la predeterminada)
  currencies: CurrencyResponse[] = [];
  brandingTemplates: QuoteTemplateResponse[] = [];
  private optionalCatalogsLoaded = false;

  // ------------------------------------------------------------
  // (INC_CRM_005 (6) inc46) Cascada negocio → contacto principal / moneda
  // ------------------------------------------------------------
  // El modal pedía el negocio y dejaba el contacto principal en blanco, obligando a buscar a mano
  // un dato que el CRM ya tiene asociado al negocio. Se replica la cascada del wizard de
  // cotizaciones (quotes-form.onDealPicked): al elegir el negocio se precarga su contacto primario
  // y su moneda, ambos editables. Una elección manual del usuario ya no se pisa.
  /** true cuando el usuario eligió el contacto a mano: la cascada del negocio ya no lo reemplaza. */
  private contactManuallyChosen = false;
  /** true cuando el usuario eligió la moneda a mano. */
  private currencyManuallyChosen = false;
  /** Moneda (`prefix`) de cada negocio devuelto por el buscador, para la cascada. */
  private dealCurrencyPrefixes = new Map<number, string>();
  /** Badge "Auto-completado" del selector de contacto. */
  contactAutoFilled = false;

  constructor(
    private templateApi: QuoteContentTemplateApiService,
    private brandingTemplateApi: QuoteTemplateApiService,
    private authService: AuthService,
    private router: Router,
    private datetimeService: DatetimeService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private dealApi: DealApiService,
    private dealContactApi: DealContactApiService,
    private contactoApi: ContactoApiService,
    private productoApi: ProductoApiService
  ) { }

  ngOnInit(): void {
    this.cargarTemplates();
  }

  cargarTemplates(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.templateApi.getAllTemplates(businessId).subscribe({
      next: (data) => {
        this.templates = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar plantillas de contenido';
        this.loading = false;
      }
    });
  }

  nuevaPlantilla(): void {
    this.router.navigate(['/cotizaciones/plantillas-contenido/crear']);
  }

  editarPlantilla(template: QuoteContentTemplateResponse): void {
    this.router.navigate(['/cotizaciones/plantillas-contenido/editar', template.id]);
  }

  async eliminarPlantilla(template: QuoteContentTemplateResponse): Promise<void> {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar plantilla',
      message: `¿Está seguro de eliminar la plantilla "${template.name}"?`
    });
    if (!ok) return;

    this.templateApi.deleteTemplate(template.id, businessId).subscribe({
      next: () => {
        this.cargarTemplates();
        this.toast.deleted('Plantilla de contenido');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar la plantilla' });
      }
    });
  }

  // Open the modal to pick a deal (+ optional currency/contact/branding) for the generated quote.
  generarCotizacion(template: QuoteContentTemplateResponse): void {
    this.generateTemplate = template;
    this.resetSeleccionModal();
    this.showGenerateModal = true;
    this.cargarCatalogosOpcionales();
  }

  /**
   * Limpia lo elegido en el modal. Las banderas "elegido a mano" se reinician aquí: el modal es una
   * única instancia reutilizada para cada plantilla, y sin esto la decisión manual de la generación
   * anterior bloquearía la cascada de la siguiente.
   */
  private resetSeleccionModal(): void {
    this.selectedDeal = null;
    this.selectedContact = null;
    this.selectedCurrencyId = null;
    this.selectedBrandingTemplateId = null;
    this.contactManuallyChosen = false;
    this.currencyManuallyChosen = false;
    this.contactAutoFilled = false;
  }

  // Catálogos para los campos opcionales del modal (carga única, perezosa).
  private cargarCatalogosOpcionales(): void {
    if (this.optionalCatalogsLoaded) return;
    this.optionalCatalogsLoaded = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.productoApi.getCurrencies().subscribe({
      // El catálogo puede llegar DESPUÉS de que el usuario elija el negocio: se vuelve a aplicar la
      // cascada de moneda (es idempotente y no pisa una elección manual).
      next: (data) => {
        this.currencies = data;
        if (this.selectedDeal) this.aplicarMonedaDelDeal(this.selectedDeal.id);
      },
      error: () => { }
    });
    this.brandingTemplateApi.getAllTemplates(businessId).subscribe({
      next: (data) => this.brandingTemplates = data,
      error: () => { }
    });
  }

  closeGenerateModal(): void {
    if (this.generating) return;
    this.showGenerateModal = false;
    this.generateTemplate = null;
    this.resetSeleccionModal();
  }

  // Searchable deal source for the entity-picker (search + Enter-for-all + infinite scroll).
  searchDeals = (term: string, page: number): Observable<PickerSearchPage> => {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    return this.dealApi.searchDeals({
      businessId,
      searchName: term?.trim() || undefined,
      page,
      size: 20
    }).pipe(
      map((res) => {
        // La moneda del negocio (`prefix`) no cabe en PickerOption: se guarda aparte para la
        // cascada, porque al seleccionar solo llega el {id, label}.
        (res.content || []).forEach((d) => {
          if (d.id != null) this.dealCurrencyPrefixes.set(d.id, d.prefix || '');
        });
        return {
          items: (res.content || []).map((d) => ({ id: d.id as number, label: d.name })),
          totalPages: res.totalPages
        };
      })
    );
  };

  /**
   * (inc46) Elegir un negocio autocompleta lo que se deriva de él: su contacto principal y su
   * moneda. Cambiar de negocio REEMPLAZA lo derivado del anterior; limpiarlo lo vacía.
   */
  onDealSelected(opt: PickerOption | null): void {
    const previousDealId = this.selectedDeal?.id ?? null;
    this.selectedDeal = opt;
    const newDealId = opt?.id ?? null;

    if (newDealId == null) {
      this.selectedContact = null;
      this.contactManuallyChosen = false;
      this.contactAutoFilled = false;
      return;
    }
    if (newDealId === previousDealId) return;

    // El contacto del negocio anterior ya no aplica, ni siquiera si se había elegido a mano.
    this.contactManuallyChosen = false;
    this.selectedContact = null;
    this.precargarContactoPrimarioDelDeal(newDealId);
    this.aplicarMonedaDelDeal(newDealId);
  }

  /**
   * Precarga el contacto primario del negocio (el que tiene `isPrimary`, o el primero). Editable.
   * Que un negocio no tenga contactos es un caso normal: el selector queda vacío, sin error.
   */
  private precargarContactoPrimarioDelDeal(dealId: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.dealContactApi.getByDeal(dealId, businessId).subscribe({
      next: (contacts) => {
        // El negocio pudo cambiar mientras respondía la petición, o el usuario ya eligió a mano.
        if (this.contactManuallyChosen || this.selectedDeal?.id !== dealId) return;
        const primary = contacts.find(c => c.isPrimary) ?? contacts[0];
        if (!primary) {
          this.selectedContact = null;
          return;
        }
        const name = [primary.contactFullname, primary.contactLastname].filter(Boolean).join(' ').trim();
        this.selectedContact = {
          id: primary.idContact,
          label: name || primary.contactMail || `Contacto #${primary.idContact}`,
          sublabel: primary.contactMail || undefined
        };
        this.contactAutoFilled = true;
      },
      error: () => { /* sin contactos del negocio: el selector queda vacío y editable */ }
    });
  }

  /**
   * Moneda de la cotización = moneda del negocio (`deal.prefix`, código ISO o símbolo), igual que
   * en el wizard. Si se omite, el backend hereda la del negocio de todos modos; fijarla aquí es
   * para que el usuario VEA en qué moneda va a nacer la cotización. No pisa una elección manual.
   */
  private aplicarMonedaDelDeal(dealId: number): void {
    if (this.currencyManuallyChosen) return;
    const prefix = (this.dealCurrencyPrefixes.get(dealId) || '').trim().toUpperCase();
    if (!prefix || this.currencies.length === 0) {
      this.selectedCurrencyId = null;
      return;
    }
    const match = this.currencies.find(c => (c.code || '').toUpperCase() === prefix)
      ?? this.currencies.find(c => (c.prefix || '').toUpperCase() === prefix);
    this.selectedCurrencyId = match ? match.id : null;
  }

  /** El usuario tocó el selector de moneda: la cascada del negocio deja de pisarlo. */
  onCurrencyManuallyChanged(): void {
    this.currencyManuallyChosen = true;
  }

  // Searchable contact source for the optional primary-contact picker.
  searchContacts = (term: string, page: number): Observable<PickerSearchPage> => {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    return this.contactoApi.searchContacts({
      businessId,
      searchTerm: term?.trim() || undefined,
      page,
      size: 20
    }).pipe(
      map((res) => ({
        items: (res.content || []).map((c) => ({
          id: c.id as number,
          label: `${c.fullname}${c.lastname ? ' ' + c.lastname : ''}`
        })),
        totalPages: res.totalPages
      }))
    );
  };

  onContactSelected(opt: PickerOption | null): void {
    this.selectedContact = opt;
    this.contactManuallyChosen = true;
    this.contactAutoFilled = false;
  }

  // Generate a draft quote from the selected template + deal, then open the quote editor.
  confirmarGenerar(): void {
    if (!this.generateTemplate || !this.selectedDeal) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: GenerateQuoteFromTemplateRequest = {
      dealId: this.selectedDeal.id,
      businessId
    };
    // Opcionales: si se omiten, el backend hereda moneda del deal y usa la plantilla predeterminada.
    if (this.selectedCurrencyId !== null) {
      request.currencyId = this.selectedCurrencyId;
    }
    if (this.selectedContact !== null) {
      request.idContactPrimary = this.selectedContact.id;
    }
    if (this.selectedBrandingTemplateId !== null) {
      request.idTemplate = this.selectedBrandingTemplateId;
    }

    this.generating = true;
    this.templateApi.generateQuote(this.generateTemplate.id, request).subscribe({
      next: (quote) => {
        this.generating = false;
        this.showGenerateModal = false;
        this.generateTemplate = null;
        this.toast.created('Cotización');
        this.router.navigate(['/cotizaciones/editar', quote.id]);
      },
      error: (err) => {
        this.generating = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo generar la cotización' });
      }
    });
  }

  // Eliminar plantillas requiere rol Admin o superior (el backend responde 403 para el resto).
  // Mismo patrón ad-hoc de roles usado en quotes-form (canManageLink) y call.constants.
  get canDelete(): boolean {
    const role = this.authService.getCurrentUser()?.roleName;
    return role === 'Admin' || role === 'Super Admin';
  }

  itemCount(template: QuoteContentTemplateResponse): number {
    return template.items?.length || 0;
  }

  formatDate(date?: string): string {
    return this.datetimeService.formatDateNumeric(date);
  }
}
